import {
  colorOptions,
  fontSizeOptions,
  fontWeightOptions,
  leadingOptions,
  textAlignOptions,
} from '@bm-js/atlascloud-ui'
import Select from './Select'
import {
  ChangeEvent,
  DynamicContentType,
  FontWeight,
  TextAlignment,
  TextColor,
} from '@bm-js/atlascloud-shared'
import { useMemo } from 'react'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'

interface Props<T> {
  content: DynamicContentType<T>
  setData: (e: ChangeEvent) => void
}

interface TextAlignmentOptionsProps<T extends { alignment?: TextAlignment }>
  extends Props<T> {
  omit?: TextAlignment[]
}

const textAlignOptionTrKeys: Record<TextAlignment, TrKey> = {
  left: TrKey.textAlignmentOptionLeft,
  right: TrKey.textAlignmentOptionRight,
  center: TrKey.textAlignmentOptionCenter,
  'center-mobile': TrKey.textAlignmentOptionCenterMobile,
}

export const TextAlignmentOptions = <T extends { alignment?: TextAlignment }>({
  content,
  setData,
  omit,
}: TextAlignmentOptionsProps<T>) => {
  const { tr } = useTr()
  const options = useMemo(() => {
    if (!omit) return textAlignOptions
    return textAlignOptions.filter((opt) => !omit.includes(opt.value))
  }, [omit])

  return (
    <Select
      light
      label={tr(TrKey.textAlignmentLabel)}
      desc={tr(TrKey.textAlignmentDescription)}
      id='alignment'
      name='alignment'
      value={content.data.alignment}
      onChange={setData}
    >
      {options.map((opt) => (
        <option key={`text_align_option_${opt.value}`} value={opt.value}>
          {tr(textAlignOptionTrKeys[opt.value])}
        </option>
      ))}
    </Select>
  )
}

const fontWeightOptionTrKeys: Record<FontWeight, TrKey> = {
  thin: TrKey.fontWeightOptionThin,
  extralight: TrKey.fontWeightOptionExtralight,
  light: TrKey.fontWeightOptionLight,
  normal: TrKey.fontWeightOptionNormal,
  medium: TrKey.fontWeightOptionMedium,
  semibold: TrKey.fontWeightOptionSemibold,
  bold: TrKey.fontWeightOptionBold,
  extrabold: TrKey.fontWeightOptionExtrabold,
  black: TrKey.fontWeightOptionBlack,
}

export const FontWeightOptions = <T extends { fontWeight?: FontWeight }>({
  content,
  setData,
}: Props<T>) => {
  const { tr } = useTr()
  return (
    <Select
      light
      label={tr(TrKey.fontWeightLabel)}
      desc={tr(TrKey.fontWeightDescription)}
      id='fontWeight'
      name='fontWeight'
      value={content.data.fontWeight}
      onChange={setData}
    >
      {fontWeightOptions.map((opt) => (
        <option key={`font_weight_option_${opt.value}`} value={opt.value}>
          {tr(fontWeightOptionTrKeys[opt.value])}
        </option>
      ))}
    </Select>
  )
}

const textColorOptionTrKeys: Record<TextColor, TrKey> = {
  black: TrKey.textColorOptionBlack,
  white: TrKey.textColorOptionWhite,
  'neutral-700': TrKey.textColorOptionNeutral700,
  'primary-400': TrKey.textColorOptionPrimary400,
  'primary-300': TrKey.textColorOptionPrimary300,
  'primary-200': TrKey.textColorOptionPrimary200,
}

export const TextColorOptions = <T extends { color?: TextColor }>({
  content,
  setData,
}: Props<T>) => {
  const { tr } = useTr()
  return (
    <Select
      light
      label={tr(TrKey.textColorLabel)}
      desc={tr(TrKey.textColorDescription)}
      id='color'
      name='color'
      value={content.data.color}
      onChange={setData}
    >
      {colorOptions.map((opt) => (
        <option key={`font_color_option_${opt.value}`} value={opt.value}>
          {tr(textColorOptionTrKeys[opt.value])}
        </option>
      ))}
    </Select>
  )
}

export const FontSizeOptions = <T extends { fontSize: string }>({
  content,
  setData,
}: Props<T>) => {
  const { tr } = useTr()
  return (
    <Select
      light
      label={tr(TrKey.fontSizeLabel)}
      desc={tr(TrKey.fontSizeDescription)}
      id='fontSize'
      name='fontSize'
      value={content.data.fontSize}
      onChange={setData}
    >
      {fontSizeOptions.map((opt) => (
        <option key={`font_size_option_${opt.value}`} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </Select>
  )
}

export const LeadingOptions = <T extends { leading: string }>({
  content,
  setData,
}: Props<T>) => {
  const { tr } = useTr()
  return (
    <Select
      light
      label={tr(TrKey.leadingLabel)}
      desc={tr(TrKey.leadingDescription)}
      id='leading'
      name='leading'
      value={content.data.leading}
      onChange={setData}
    >
      {leadingOptions.map((opt) => (
        <option key={`font_leading_option_${opt.value}`} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </Select>
  )
}
